/**
 * Intake API client (ATLAS-100, U35). Every intake screen reads and writes
 * through here.
 *
 * No function takes a person: the server takes the actor from the sign-in
 * token (ADR 0008), so there is no `by` or `email` to pass (U35 criterion 1).
 *
 * Each page unit adds the calls it uses, in this pattern (plan Q2). The team
 * page's calls are near the end (ATLAS-106); the idea page's review team and
 * admin calls are last (ATLAS-104).
 */
import type { AxiosRequestConfig } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type {
  IntakeBoardColumn,
  IntakeCheckMarkInput,
  IntakeCheckMarkReply,
  IntakeColumnRead,
  IntakeDecisionInput,
  IntakeEditInput,
  IntakeFeedRead,
  IntakeFeedSort,
  IntakeFeedStage,
  IntakeFollowInput,
  IntakeFollowView,
  IntakeItemActionReply,
  IntakeItemDetail,
  IntakeLeaderboard,
  IntakeMakePrivateInput,
  IntakeMakePrivateResult,
  IntakeMe,
  IntakeNeedResult,
  IntakePriorityInput,
  IntakeRequestInfoInput,
  IntakeReviewCycle,
  IntakeReviewCycleInput,
  IntakeReviewerAnswerInput,
  IntakeRouteConfirmInput,
  IntakeSenderEditInput,
  IntakeSensitiveResolveInput,
  IntakeSensitiveResolveReply,
  IntakeSubmitInput,
  IntakeTeamView,
  IntakeWeeklyPass,
} from '@jmtest/shared-types';
import { acquireGraphTokenSilent } from '@/auth/msalConfig';
import apiClient from './client';

export const INTAKE_API = '/api/v1/intake';

/** Intake calls give up after 30 seconds, not the app's 120 (plan Q8). Submit is idempotent, so a retry after a timeout can't duplicate. */
export const INTAKE_TIMEOUT_MS = 30_000;

/** Shown for any 5xx or network failure, never the server's own text (plan Q9). */
export const INTAKE_UNAVAILABLE_MESSAGE = "Intake isn't available right now. Try again in a few minutes.";

const FALLBACK_MESSAGE = "That didn't work. Try again.";

/** Shown when the sign-in token can't be renewed without the person (ATLAS-100b). */
export const SIGN_IN_EXPIRED_MESSAGE = 'Your sign-in has expired.';

/** MSAL's codes for "silent renewal failed; the person has to sign in" (ATLAS-100b). */
const SIGN_IN_REQUIRED_CODES = new Set(['interaction_required', 'login_required', 'consent_required']);

/**
 * True for an MSAL error that needs the person to sign in again. Today the
 * shared request interceptor swallows these and sends the call without a token,
 * so they arrive as a 401; this catches one that gets through.
 */
export function isSignInRequired(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const code = err && typeof err === 'object' ? (err as { errorCode?: unknown }).errorCode : undefined;
  return typeof code === 'string' && SIGN_IN_REQUIRED_CODES.has(code);
}

/**
 * A failed intake call, with the status the shared client would otherwise
 * drop: 404 for "can't see it" (D-58), 503 for "unavailable" (D-113), 400
 * field names for the form, 409 for "someone else got there first".
 * `status` is 0 for a network failure or timeout.
 */
export class IntakeApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** Fields a 400 VALIDATION_ERROR named (`error.details.issues[].path[0]`); empty otherwise. */
  readonly fields: string[];

  constructor(status: number, message: string, code: string | null = null, fields: string[] = []) {
    super(message);
    this.name = 'IntakeApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

interface ErrorBody {
  error?: {
    code?: unknown;
    message?: unknown;
    details?: { issues?: Array<{ path?: unknown[] }> };
  };
}

function fieldsOf(body: ErrorBody | undefined): string[] {
  const issues = body?.error?.details?.issues;
  if (!Array.isArray(issues)) return [];
  const names = issues
    .map((issue) => (Array.isArray(issue?.path) ? issue.path[0] : undefined))
    .filter((p): p is string | number => typeof p === 'string' || typeof p === 'number')
    .map(String);
  return [...new Set(names)];
}

function errorFrom(status: number, data: unknown): IntakeApiError {
  const body = (data && typeof data === 'object' ? data : undefined) as ErrorBody | undefined;
  const code = typeof body?.error?.code === 'string' ? body.error.code : null;
  if (status >= 500) return new IntakeApiError(status, INTAKE_UNAVAILABLE_MESSAGE, code);
  const serverMessage = typeof body?.error?.message === 'string' && body.error.message ? body.error.message : null;
  return new IntakeApiError(status, serverMessage ?? FALLBACK_MESSAGE, code, status === 400 ? fieldsOf(body) : []);
}

/**
 * One intake call through the shared `apiClient` (plan Q1 A, so `client.ts`
 * is untouched). Every status but 401 comes back as a response, so the status
 * survives; 401 still reaches the shared interceptor, which starts re-login.
 * A 2xx arrives already unwrapped from `{ success, data }`.
 */
export async function intakeRequest<T>(config: AxiosRequestConfig): Promise<T> {
  // Set by axios only when a response arrived; stays undefined for a network failure or timeout.
  let answeredWith: number | undefined;
  let response;
  try {
    response = await apiClient.request<T>({
      ...config,
      timeout: INTAKE_TIMEOUT_MS,
      validateStatus: (status) => {
        answeredWith = status;
        return status !== 401;
      },
    });
  } catch (err) {
    if (answeredWith === 401) {
      throw new IntakeApiError(401, err instanceof Error ? err.message : FALLBACK_MESSAGE, 'UNAUTHORIZED');
    }
    // MSAL couldn't renew the token without the person (ATLAS-100b). Read as a 401, not a network failure.
    if (isSignInRequired(err)) throw new IntakeApiError(401, SIGN_IN_EXPIRED_MESSAGE, 'SIGN_IN_REQUIRED');
    throw new IntakeApiError(0, INTAKE_UNAVAILABLE_MESSAGE, null);
  }
  if (response.status >= 400) throw errorFrom(response.status, response.data);
  return response.data;
}

/** GET /api/v1/intake/me: the caller's intake role. `role: null` means no intake for them (D-112). */
export async function fetchIntakeMe(): Promise<IntakeMe> {
  return intakeRequest<IntakeMe>({ method: 'get', url: `${INTAKE_API}/me` });
}

/** GET /api/v1/intake?column=: one board column as the caller may see it; `mine` narrows to their own items. */
export async function fetchIntakeColumn(
  column: IntakeBoardColumn,
  { mine = false }: { mine?: boolean } = {},
): Promise<IntakeColumnRead> {
  return intakeRequest<IntakeColumnRead>({
    method: 'get',
    url: INTAKE_API,
    params: { column, mine: mine ? 'true' : 'false' },
  });
}

/** GET /api/v1/intake/:code. A 404 means it doesn't exist or the caller can't see it, and the two look the same (D-58). */
export async function fetchIntakeItem(code: string): Promise<IntakeItemDetail> {
  return intakeRequest<IntakeItemDetail>({ method: 'get', url: `${INTAKE_API}/${encodeURIComponent(code)}` });
}

export interface IntakeSubmitOptions {
  /** One key per form attempt, reused on retry, so a resend can't make a second idea (ATLAS-69 Q3). */
  idempotencyKey: string;
  /**
   * From `acquireGraphTokenSilent()`, if the caller got one. Without it the
   * idea still saves and its location is blank (ATLAS-69 Q2).
   */
  graphToken?: string | null;
}

/** POST /api/v1/intake: one idea. Answers 201 with the item as the submitter reads it. */
export async function submitIdea(
  input: IntakeSubmitInput,
  { idempotencyKey, graphToken }: IntakeSubmitOptions,
): Promise<IntakeItemDetail> {
  return intakeRequest<IntakeItemDetail>({
    method: 'post',
    url: INTAKE_API,
    data: input,
    headers: {
      'Idempotency-Key': idempotencyKey,
      ...(graphToken ? { 'X-Graph-Token': graphToken } : {}),
    },
  });
}

/** The lists an admin can change, as the URL names them. `admins` matches no route (ATLAS-111). */
export type IntakeTeamListPath = 'reviewers' | 'sponsors' | 'pilots';

/** GET /api/v1/intake/team: every list and the change log, newest first. Any employee may read it (D-67). */
export async function fetchTeam(): Promise<IntakeTeamView> {
  return intakeRequest<IntakeTeamView>({ method: 'get', url: `${INTAKE_API}/team` });
}

/** Code for a Graph token the browser couldn't get, so the add was never sent. Client-side only. */
export const GRAPH_TOKEN_UNAVAILABLE = 'GRAPH_TOKEN_UNAVAILABLE';

/**
 * POST /api/v1/intake/team/{list}: add one person by Entra object id. Only the
 * id goes; the server reads the name and email from Graph with the admin's own
 * token (ATLAS-111). A token the browser can't get stops the add here, rather
 * than sending a request the server has to refuse. Answers the whole team view.
 */
export async function addTeamMember(list: IntakeTeamListPath, personOid: string): Promise<IntakeTeamView> {
  let graphToken: string;
  try {
    graphToken = await acquireGraphTokenSilent();
  } catch {
    throw new IntakeApiError(0, FALLBACK_MESSAGE, GRAPH_TOKEN_UNAVAILABLE);
  }
  return intakeRequest<IntakeTeamView>({
    method: 'post',
    url: `${INTAKE_API}/team/${list}`,
    data: { personOid },
    headers: { 'X-Graph-Token': graphToken },
  });
}

/** DELETE /api/v1/intake/team/{list}/:oid. Answers the whole team view. */
export async function removeTeamMember(list: IntakeTeamListPath, oid: string): Promise<IntakeTeamView> {
  return intakeRequest<IntakeTeamView>({ method: 'delete', url: `${INTAKE_API}/team/${list}/${encodeURIComponent(oid)}` });
}

// ── The ideas feed and "I need this too" (ATLAS-101, U36, on ATLAS-87's routes) ─

/** The query the feed page sends. `limit` stays the server's 50; `period` is ATLAS-101b's. */
export interface IntakeFeedParams {
  sort: IntakeFeedSort;
  mine?: boolean;
  /** One stage; none means every stage but closed (D-143 (8)). */
  status?: IntakeFeedStage;
  /** `nextCursor` from the page before. A 400 FEED_CURSOR_EXPIRED means start again from page 1. */
  cursor?: string | null;
  /** ATLAS-101b: `week` with `sort: 'top'` is "Top ideas this week"; items then carry `weekVotes`. */
  period?: 'week' | 'all';
  /** ATLAS-101b: fewer than the server's 50, for the weekly strip's three. */
  limit?: number;
}

/** GET /api/v1/intake/feed: one page of the feed as the caller may see it. */
export async function fetchFeed({ sort, mine = false, status, cursor, period, limit }: IntakeFeedParams): Promise<IntakeFeedRead> {
  return intakeRequest<IntakeFeedRead>({
    method: 'get',
    url: `${INTAKE_API}/feed`,
    params: {
      sort,
      mine: mine ? 'true' : 'false',
      ...(status ? { status } : {}),
      ...(cursor ? { cursor } : {}),
      ...(period ? { period } : {}),
      ...(limit !== undefined ? { limit: String(limit) } : {}),
    },
  });
}

/** GET /api/v1/intake/feed/leaderboard?period=month: public, checked ideas this Central month by location (ATLAS-101b, D-368). */
export async function fetchLeaderboard(): Promise<IntakeLeaderboard> {
  return intakeRequest<IntakeLeaderboard>({ method: 'get', url: `${INTAKE_API}/feed/leaderboard`, params: { period: 'month' } });
}

/**
 * POST /api/v1/intake/:code/contributions {type:'need_too'}: "I need this
 * too". The voter is the sign-in token. The voter's own Graph token goes too
 * when the browser can get one, for their office only (D-151); without it the
 * vote still counts, so a token failure never stops the vote.
 */
export async function voteNeed(code: string): Promise<IntakeNeedResult> {
  let graphToken: string | null = null;
  try {
    graphToken = await acquireGraphTokenSilent();
  } catch {
    graphToken = null;
  }
  return intakeRequest<IntakeNeedResult>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/contributions`,
    data: { type: 'need_too' },
    ...(graphToken ? { headers: { 'X-Graph-Token': graphToken } } : {}),
  });
}

/** DELETE /api/v1/intake/:code/need: take your own vote back. Answers 200 whether there was a vote or not. */
export async function unvoteNeed(code: string): Promise<IntakeNeedResult> {
  return intakeRequest<IntakeNeedResult>({ method: 'delete', url: `${INTAKE_API}/${encodeURIComponent(code)}/need` });
}

// ── The idea page (ATLAS-103, U38) ───────────────────────────────────────────

/**
 * POST /api/v1/intake/:code/questions/:qid/answer: the sender or forwarder
 * answers one review team question (ATLAS-78). The text goes as typed; the
 * server trims it and takes 1 to 2000 characters. One key per question and
 * text, so a resend after a timeout can't store a second answer. Answers the
 * item as the caller reads it.
 */
export async function answerReviewerQuestion(
  code: string,
  questionId: string,
  answer: string,
  { idempotencyKey }: { idempotencyKey: string },
): Promise<IntakeItemDetail> {
  return intakeRequest<IntakeItemDetail>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/questions/${encodeURIComponent(questionId)}/answer`,
    data: { answer } satisfies IntakeReviewerAnswerInput,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** Code for "already private" (ATLAS-85): a retry that found its own write done (D-140). */
export const INTAKE_ALREADY_PRIVATE = 'INTAKE_ALREADY_PRIVATE';

/** POST /api/v1/intake/:code/private: make an item private, with a reason of 5 to 1000 characters (ATLAS-85). */
export async function makePrivate(code: string, reason: string): Promise<IntakeMakePrivateResult> {
  return intakeRequest<IntakeMakePrivateResult>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/private`,
    data: { reason } satisfies IntakeMakePrivateInput,
  });
}

/**
 * PUT /api/v1/intake/:code/follow: stop (`false`) or restart (`true`) this
 * idea's emails for the caller (ATLAS-77, D-31). The person is the token. A
 * repeat does no harm: stopping twice keeps one row, restarting twice none.
 * The idea page's last client call (ATLAS-100 plan Q2, D-158), added by ATLAS-103b.
 */
export async function setFollow(code: string, follow: boolean): Promise<IntakeFollowView> {
  return intakeRequest<IntakeFollowView>({
    method: 'put',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/follow`,
    data: { follow } satisfies IntakeFollowInput,
  });
}

// ── The review team's and admins' panels (ATLAS-104, U39) ───────────────────

/**
 * GET /api/v1/intake/review: the weekly pass (ATLAS-80), reviewers and
 * admins. The idea page reads its `routesToConfirm` to learn whether this
 * item has a route waiting and which team it names, because the item read
 * doesn't carry the suggestion (D-177).
 */
export async function fetchWeeklyPass(): Promise<IntakeWeeklyPass> {
  return intakeRequest<IntakeWeeklyPass>({ method: 'get', url: `${INTAKE_API}/review` });
}

/**
 * POST /api/v1/intake/review/cycles: an admin sets up a monthly review (ATLAS-83,
 * plan Q5). 409 INTAKE_REVIEW_EXISTS when that month or date already has one.
 */
export async function createReviewCycle(input: IntakeReviewCycleInput): Promise<IntakeReviewCycle> {
  return intakeRequest<IntakeReviewCycle>({ method: 'post', url: `${INTAKE_API}/review/cycles`, data: input });
}

/** POST /api/v1/intake/:code/route: confirm (`true`) or reject (`false`) the route triage suggested (ATLAS-80). */
export async function confirmRoute(code: string, confirm: boolean): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/route`,
    data: { confirm } satisfies IntakeRouteConfirmInput,
  });
}

/** POST /api/v1/intake/:code/request-info: the review team's question to the sender, 5 to 1000 characters (ATLAS-80). */
export async function askSender(code: string, text: string): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/request-info`,
    data: { text } satisfies IntakeRequestInfoInput,
  });
}

/** PATCH /api/v1/intake/:code: title, systems and the review team's summary, only the keys that changed (ATLAS-81). */
export async function editItem(code: string, input: IntakeEditInput): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({ method: 'patch', url: `${INTAKE_API}/${encodeURIComponent(code)}`, data: input });
}

/** PUT /api/v1/intake/:code/priority: the ranking inputs. A form filled from an older save gets 409 (ATLAS-81, D-157). */
export async function setPriority(code: string, input: IntakePriorityInput): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({ method: 'put', url: `${INTAKE_API}/${encodeURIComponent(code)}/priority`, data: input });
}

/** POST /api/v1/intake/:code/decision: accept, park or decline, with the version the screen read (ATLAS-82, D-172). */
export async function decideItem(code: string, input: IntakeDecisionInput): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({ method: 'post', url: `${INTAKE_API}/${encodeURIComponent(code)}/decision`, data: input });
}

/**
 * POST /api/v1/intake/:code/sensitive/resolve: an admin's choice on a private item (ATLAS-86).
 * A publish carries the version the admin read (D-300); 409 INTAKE_ITEM_CHANGED if it changed since.
 */
export async function resolvePrivate(code: string, input: IntakeSensitiveResolveInput): Promise<IntakeSensitiveResolveReply> {
  return intakeRequest<IntakeSensitiveResolveReply>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/sensitive/resolve`,
    data: input satisfies IntakeSensitiveResolveInput,
  });
}

/** POST /api/v1/intake/:code/check: an admin marks the check cleared or flagged, while AI is off (ATLAS-86, D-132, D-168). */
export async function markChecked(code: string, input: IntakeCheckMarkInput): Promise<IntakeCheckMarkReply> {
  return intakeRequest<IntakeCheckMarkReply>({ method: 'post', url: `${INTAKE_API}/${encodeURIComponent(code)}/check`, data: input });
}

// ── The sender's own edit and withdraw (ATLAS-125, U46) ──────────────────────

/**
 * PATCH /api/v1/intake/:code/words: the sender changes their own words, only
 * the fields that changed. 409 INTAKE_EDIT_LOCKED once someone else has voted
 * or the review team has acted; its message is the page's words. One key per
 * attempt, reused on a resend, so a retry after a timeout is applied once.
 */
export async function editOwnIdea(
  code: string,
  input: IntakeSenderEditInput,
  { idempotencyKey }: { idempotencyKey: string },
): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({
    method: 'patch',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/words`,
    data: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** POST /api/v1/intake/:code/withdraw: the sender withdraws their idea. A repeat is a 200 replay. 409 INTAKE_NOT_OPEN once it's closed. */
export async function withdrawOwnIdea(code: string, { idempotencyKey }: { idempotencyKey: string }): Promise<IntakeItemActionReply> {
  return intakeRequest<IntakeItemActionReply>({
    method: 'post',
    url: `${INTAKE_API}/${encodeURIComponent(code)}/withdraw`,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}
